// PublicOnlyRoute.jsx (DROP-IN)
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

const MFA_PENDING_KEY = "nx_mfa_pending";

function isClientMfaPending() {
  try {
    return sessionStorage.getItem(MFA_PENDING_KEY) === "1";
  } catch {
    return false;
  }
}

export default function PublicOnlyRoute({ children, redirectTo = "/" }) {
  const { currentUser, loading } = useAuth();
  const loc = useLocation();

  if (loading) return null;

  // ✅ CRITICAL: while MFA is pending, do NOT redirect away from /login
  // Treat as unauthenticated even if Firebase provider briefly signs in.
  const mfaPending = isClientMfaPending();
  if (mfaPending) {
    console.log("PublicOnlyRoute", { path: loc.pathname, isAuthed: false, mfaPending: true, user: currentUser });
    return children ?? <Outlet />;
  }

  const isAuthed = !!(currentUser && (currentUser.uid || currentUser._source === "cookie"));
  console.log("PublicOnlyRoute", { path: loc.pathname, isAuthed, mfaPending: false, user: currentUser });

  if (isAuthed) {
    // ✅ 1) Strongest: stored deep link (survives tab/refresh)
    try {
      const stored = localStorage.getItem("postLoginRedirect");
      if (stored && typeof stored === "string" && stored.trim()) {
        localStorage.removeItem("postLoginRedirect");
        return <Navigate to={stored} replace />;
      }
    } catch {
      // ignore storage failures
    }

    // ✅ 2) Next: router state (works in same-tab transitions)
    const fromState = loc.state && loc.state.from;

    // from can be either a string OR a location-like object
    if (typeof fromState === "string" && fromState.trim()) {
      return <Navigate to={fromState} replace />;
    }

    if (fromState && typeof fromState === "object" && fromState.pathname) {
      const target = fromState.pathname + (fromState.search || "");
      return <Navigate to={target} replace />;
    }

    // ✅ 3) Default
    return <Navigate to={redirectTo} replace />;
  }

  return children ?? <Outlet />;
}
